import { useEffect, useRef } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { LoadingSpinner } from '@/components/ui/loading-spinner'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRole?: 'admin' | 'user'
}

export function ProtectedRoute({
  children,
  requiredRole,
}: ProtectedRouteProps) {
  const { isAuthenticated, isAdmin, isLoading, profile } = useAuth()
  const { isImpersonating } = useImpersonation()
  const location = useLocation()
  const { toast } = useToast()
  const hasShownToast = useRef(false)

  // Determine redirect reason
  const isDeactivated = isAuthenticated && (!profile || profile.status !== 'active')
  const needsAdmin = requiredRole === 'admin' && !isAdmin

  useEffect(() => {
    if (hasShownToast.current) return

    if (isDeactivated) {
      toast({ title: 'Error', description: 'Your account has been deactivated', variant: 'destructive' })
      hasShownToast.current = true
    } else if (needsAdmin) {
      toast({ title: 'Error', description: 'Access denied. Admin privileges required.', variant: 'destructive' })
      hasShownToast.current = true
    }
  }, [isDeactivated, needsAdmin, toast])

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Check if user/account is deactivated
  if (isDeactivated) {
    return <Navigate to="/login" replace />
  }

  // Role-based access control
  if (needsAdmin) {
    return <Navigate to="/dashboard" replace />
  }

  if (requiredRole === 'user') {
    // Admins can access user routes only when impersonating
    if (isAdmin && !isImpersonating) {
      return <Navigate to="/admin" replace />
    }
  }

  return <>{children}</>
}
